// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import { useEvolvePaginatedGet } from '@evolvemep/api-ts';

import type { GlobalFilterProps } from 'components/Mantine/GlobalFilters/types';
import { SearchableMultiSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableMultiSelect';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { useUser } from 'hooks/user/useUser';

export const FacilitiesFilter = ({ filter, value, onChange }: GlobalFilterProps<'facilities'>) => {
  const { user } = useUser();

  const facilityPaginatedGet = useEvolvePaginatedGet('shop/facility', {
    defaultConfig: {
      params: { orderBy: ['currentUserAssigned:desc', 'facilityName:asc'] },
    },
  });
  const hasGroups = facilityPaginatedGet.data.some((f) =>
    f.members.some((assignee) => assignee.userId === user.userId),
  );
  const hasAccess = useHasAccess('facilities', 'read');

  if (!hasAccess || !filter) return null;

  return (
    <SearchableMultiSelect
      placeholder="All Facilities"
      clearable
      label="Facilities"
      comboboxProps={{ withinPortal: false }}
      getGroup={
        hasGroups
          ? (f) =>
              f.members.some((assignee) => assignee.userId === user.userId) ? 'My facilities' : 'Other facilities'
          : undefined
      }
      paginatedGet={facilityPaginatedGet}
      value={value}
      onChange={onChange}
      idKey="facilityId"
      getItemLabel={(i) => i.facilityName}
      hidePickedOptions={false}
      preloadData
    />
  );
};
